/**
 * AOI (Area of Interest) API Hooks
 *
 * React Query hooks for AOI CRUD operations
 * Based on API docs: https://mavlink-be.democoncept.id/swagger-ui/index.html
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiDelete } from '@/lib/api-client';
import { aoiQueryKeys } from './aoi.keys';
import { AOIPaginatedResponse } from '../types';

// =============================================================================
// API Types (based on Swagger documentation)
// =============================================================================

/**
 * AOI response from API
 */
export interface AOIResponse {
    aoiId: string;
    aoiName: string;
    description?: string;
    boundaryGeoJson?: string;
    centerLatitude?: number;
    centerLongitude?: number;
    flightCount: number;
    createdAt: string;
    updatedAt: string;
}

/**
 * AOI create request
 */
export interface AOICreateRequest {
    aoiName: string;
    description?: string;
    boundaryGeoJson?: string;
    centerLatitude?: number;
    centerLongitude?: number;
}

/**
 * AOI update request
 */
export interface AOIUpdateRequest {
    aoiName?: string;
    description?: string;
    boundaryGeoJson?: string;
    centerLatitude?: number;
    centerLongitude?: number;
}

/**
 * Paginated AOI response
 */
export interface PageAOIResponse {
    content: AOIResponse[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
    numberOfElements: number;
    first: boolean;
    last: boolean;
    empty: boolean;
}

/**
 * AOI list query params
 */
export interface AOIListParams {
    page?: number;
    size?: number;
    search?: string;
}

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Fetch paginated list of AOIs
 */
export function useAOIsQuery(params: AOIListParams = {}) {
    const { page = 0, size = 20, search } = params;

    return useQuery({
        queryKey: aoiQueryKeys.list(params),
        queryFn: async () => {
            const queryParams = new URLSearchParams();
            queryParams.set('page', String(page));
            queryParams.set('size', String(size));
            if (search) queryParams.set('search', search);

            return apiGet<PageAOIResponse>(`/api/aoi?${queryParams.toString()}`);
        },
        staleTime: 2 * 60 * 1000, // 2 minutes
    });
}

/**
 * Fetch paginated list of AOIs with search and pagination
 */
export function useAOIListQuery(page: number = 0, size: number = 10, search: string = '') {
  return useQuery({
    queryKey: aoiQueryKeys.list({ page, size, search }),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('size', size.toString());
      if (search) {
        params.append('search', search);
      }
      return apiGet<AOIPaginatedResponse>(`/api/aoi?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch a single AOI by ID
 */
export function useAOIQuery(id: string | null) {
    return useQuery({
        queryKey: aoiQueryKeys.detail(id || ''),
        queryFn: async () => {
            if (!id) throw new Error('AOI ID is required');
            return apiGet<AOIResponse>(`/api/aoi/${id}`);
        },
        enabled: !!id,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Create a new AOI
 */
export function useCreateAOIMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: AOICreateRequest) => {
            return apiPost<AOIResponse>('/api/aoi', data);
        },
        onSuccess: () => {
            // Invalidate AOI list to refetch
            queryClient.invalidateQueries({
                queryKey: aoiQueryKeys.lists(),
            });
        },
        onError: (error) => {
            console.error('[AOI API] Create failed:', error);
        },
    });
}

/**
 * Update an AOI
 */
export function useUpdateAOIMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: AOIUpdateRequest }) => {
            return apiPut<AOIResponse>(`/api/aoi/${id}`, data);
        },
        onSuccess: (data) => {
            // Invalidate and refetch the specific AOI
            queryClient.invalidateQueries({
                queryKey: aoiQueryKeys.detail(data.aoiId),
            });
            // Also invalidate all AOI list
            queryClient.invalidateQueries({
                queryKey: aoiQueryKeys.lists(),
            });
        },
        onError: (error) => {
            console.error('[AOI API] Update failed:', error);
        },
    });
}

/**
 * Delete an AOI
 */
export function useDeleteAOIMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            return apiDelete<void>(`/api/aoi/${id}`);
        },
        onSuccess: (_, id) => {
            // Invalidate the deleted AOI
            queryClient.invalidateQueries({
                queryKey: aoiQueryKeys.detail(id),
            });
            // Invalidate all AOI list
            queryClient.invalidateQueries({
                queryKey: aoiQueryKeys.lists(),
            });
        },
        onError: (error) => {
            console.error('[AOI API] Delete failed:', error);
        },
    });
}
